import { computed, Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export const COOKIE_CONSENT_STORAGE_KEY = 'hytec_cookie_consent_v1';

export type CookieConsent = {
  version: 1;
  essential: true;
  analytics: boolean;
  decidedAt: string;
};

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  readonly consent = signal<CookieConsent | null>(null);
  readonly preferencesOpen = signal(false);
  readonly preferencesAnalytics = signal(false);
  readonly ready = signal(false);
  readonly hasDecision = computed(() => this.consent() !== null);
  readonly analyticsAllowed = computed(() => this.consent()?.analytics === true);

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object) {
    if (isPlatformBrowser(this.platformId)) {
      this.restore();
      this.ready.set(true);
    }
  }

  acceptAll(): void {
    this.save(true);
  }

  rejectNonEssential(): void {
    this.save(false);
  }

  savePreferences(analytics: boolean): void {
    this.save(analytics);
  }

  setPreferencesAnalytics(analytics: boolean): void {
    this.preferencesAnalytics.set(analytics);
  }

  openPreferences(): void {
    this.preferencesAnalytics.set(this.analyticsAllowed());
    this.preferencesOpen.set(true);
  }

  closePreferences(): void {
    this.preferencesOpen.set(false);
  }

  private restore(): void {
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<CookieConsent>;
      if (
        parsed.version !== 1 ||
        parsed.essential !== true ||
        typeof parsed.analytics !== 'boolean'
      ) {
        localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
        return;
      }

      this.consent.set({
        version: 1,
        essential: true,
        analytics: parsed.analytics,
        decidedAt:
          typeof parsed.decidedAt === 'string' ? parsed.decidedAt : new Date().toISOString(),
      });
    } catch {
      localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
    }
  }

  private save(analytics: boolean): void {
    const next: CookieConsent = {
      version: 1,
      essential: true,
      analytics,
      decidedAt: new Date().toISOString(),
    };

    this.consent.set(next);
    this.preferencesOpen.set(false);

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(next));
    }
  }
}
